/**
 * The frame of a pop-out window (popout.ts): the routed page under one
 * compact, always-dark header (office, selected patient, follow or lock,
 * sync status, account) instead of the side nav and full top bar. The page
 * itself is the same as in the main window (ShellFrame `RouteBody`: office
 * scope, permission and add-on gates included).
 *
 * Left out on purpose: the call pop, assistant, chat, notifications and the
 * install prompt, which the main window already shows (two pops for one
 * call would be worse than one).
 */
import { useBlocker, useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { PRODUCT } from '@velora/contracts/product';
import { BrandMark, ToastHost } from '@velora/ui';
import { ExternalLink, Link2, Lock } from 'lucide-react';
import { useState } from 'react';
import { PaletteHost, type PaletteMode } from '../commands/PaletteHost.js';
import { useActiveRoute } from '../layout/activeRoute.js';
import { AppearanceSwitch, QuickNavBindings } from '../layout/AppearanceSwitch.js';
import { PatientBar, SelectedPatientSync } from '../layout/PatientBar.js';
import { RouteBody } from '../layout/ShellFrame.js';
import { FreshnessControl, OfficeControl, UserMenu } from '../layout/TopBarParts.js';
import { SelectPatientHost } from '../select-patient/SelectPatientHost.js';
import { useShortcut } from '../shortcuts/registry.js';
import { ShortcutListener, ShortcutSheetHost } from '../shortcuts/ShortcutHost.js';
import { mainHref, PIN_PARAM, popoutSpec, type PopoutState } from './popout.js';
import { WindowSync } from './WindowSync.js';
import { savePinnedDefault } from './workspace.js';
import './windows.css';

/** Asks the window that opened this pop-out to show `href`; false when there is none. */
function showInOpener(href: string): boolean {
  try {
    const opener = window.opener as Window | null;
    if (!opener || opener.closed) return false;
    opener.postMessage({ t: 'velora-open-main', href }, window.location.origin);
    opener.focus();
    return true;
  } catch {
    return false;
  }
}

/**
 * A pop-out stays on its module: a link to another module (the schedule's
 * Open chart, the imaging page's Chart button) opens in the main window, so
 * the schedule on the second monitor stays a schedule. With no main window
 * left (it was closed), the pop-out goes there itself.
 */
function useStayOnModule(key: string) {
  const router = useRouter();
  useBlocker({
    enableBeforeUnload: false,
    shouldBlockFn: ({ next, action }) => {
      if (action !== 'PUSH' && action !== 'REPLACE') return false;
      const route = (
        router.routesById as Record<
          string,
          { options?: { staticData?: { shell?: { moduleId: string } } } }
        >
      )[next.routeId];
      const moduleId = route?.options?.staticData?.shell?.moduleId;
      if (!moduleId || moduleId === key) return false;
      const href = router.buildLocation({
        to: next.pathname,
        search: next.search,
      } as never).href;
      return showInOpener(mainHref(href));
    },
  });
}

export function PopoutFrame({ popout }: { popout: PopoutState }) {
  useStayOnModule(popout.key);
  const active = useActiveRoute();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteMode, setPaletteMode] = useState<PaletteMode>('patients');
  const [officeOpen, setOfficeOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const spec = popoutSpec(popout.key);
  const title = active?.route.title ?? spec?.label ?? PRODUCT.name;
  const followsPatient = Boolean(spec?.patientPath);
  const allowAll = active ? active.route.officeScope !== 'location' : true;

  const openPalette = (mode: PaletteMode) => {
    setPaletteMode(mode);
    setPaletteOpen(true);
  };
  useShortcut('mod+k', 'Search patients or jump to', () => openPalette('all'), {
    section: 'Global',
    id: 'popout.palette',
    allowInInputs: true,
  });

  const setPinned = (pinned: boolean) => {
    savePinnedDefault(popout.key, pinned);
    void navigate({
      to: location.pathname,
      // pin=0 rather than no pin: the root route keeps a missing pin (router.tsx).
      search: (prev: Record<string, unknown>) => ({ ...prev, [PIN_PARAM]: pinned ? 1 : 0 }),
      replace: true,
    } as never);
  };

  const here = `${location.pathname}${location.searchStr ?? ''}`;
  const openInMain = () => {
    const href = mainHref(here);
    if (!showInOpener(href)) window.open(href, '_blank', 'noopener');
  };

  return (
    <div className="vel-shell vel-popout" data-popout={popout.key}>
      <a className="vel-skip" href="#main">
        Skip to content
      </a>
      <div className="vel-shell__top">
        <header className="vel-topbar vel-popout__bar">
          <div className="vel-popout__start">
            <span className="vel-popout__brand" title={`${PRODUCT.name} pop-out window`}>
              <BrandMark name={PRODUCT.shortName} size={20} />
            </span>
            <span className="vel-popout__title">{title}</span>
            <OfficeControl allowAll={allowAll} open={officeOpen} onOpenChange={setOfficeOpen} />
            <PatientBar onSearch={() => openPalette('patients')} />
            {followsPatient ? (
              <div className="vel-popout__follow" role="group" aria-label="Patient in this window">
                <button
                  type="button"
                  className="vel-popout__seg"
                  aria-pressed={!popout.pinned}
                  onClick={() => setPinned(false)}
                  title="Follow the patient selected in the other Velora windows"
                >
                  <Link2 size={14} aria-hidden />
                  <span className="vel-popout__segtext">Follow</span>
                </button>
                <button
                  type="button"
                  className="vel-popout__seg"
                  aria-pressed={popout.pinned}
                  onClick={() => setPinned(true)}
                  title="Keep this patient here whatever the other windows select"
                >
                  <Lock size={13} aria-hidden />
                  <span className="vel-popout__segtext">Lock</span>
                </button>
              </div>
            ) : null}
          </div>
          <div className="vel-popout__end">
            <FreshnessControl />
            <AppearanceSwitch />
            <button
              type="button"
              className="shell-iconbtn"
              onClick={openInMain}
              aria-label="Show this page in the main window"
              title="Show this page in the main window"
            >
              <ExternalLink size={16} aria-hidden />
            </button>
            <UserMenu onShortcuts={() => setSheetOpen(true)} />
          </div>
        </header>
      </div>
      <div className="vel-shell__body">
        <main id="main" className="vel-shell__main" tabIndex={-1}>
          <RouteBody />
        </main>
      </div>
      <WindowSync popout={popout} label={title} />
      <QuickNavBindings />
      <SelectedPatientSync />
      <SelectPatientHost onPalette={() => openPalette('patients')} />
      <ShortcutListener />
      <PaletteHost open={paletteOpen} mode={paletteMode} onOpenChange={setPaletteOpen} />
      <ShortcutSheetHost open={sheetOpen} onOpenChange={setSheetOpen} screenName={title} />
      <ToastHost />
    </div>
  );
}
